import React from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Seo from '../../components/Seo';
import AccountSettings from './components/AccountSettings';
import ImagePersonaSettings from './components/ImagePersonaSettings';
import { useAuth } from '../../auth/AuthProvider';
import { useUserProfileDashboardData } from '../../hooks/useUserProfileDashboardData';
import TopTabs from '../../components/user-profile-dashboard/TopTabs';
import OverviewSection from '../../components/user-profile-dashboard/OverviewSection';
import AchievementsSection from '../../components/user-profile-dashboard/achievements/AchievementsSection';
import NeighborsSection from '../../components/user-profile-dashboard/neighbors/NeighborsSection';
import ProofDetailModal from '../../components/user-profile-dashboard/ProofDetailModal';

const UserProfileDashboard = () => {
  const { user } = useAuth();
  const dashboard = useUserProfileDashboardData({ userId: user?.id });

  const tabs = [
    { id: 'overview', label: '개요', icon: 'LayoutDashboard' },
    { id: 'achievements', label: '업적', icon: 'Award' },
    { id: 'neighbors', label: '이웃', icon: 'Users' },
    { id: 'image_settings', label: '이미지 생성 관리', icon: 'Image' },
    { id: 'settings', label: '설정', icon: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="내 프로필 - 성과 관리 | 꿈현.Labs"
        description="꿈현.Labs 내 프로필 페이지. 목표 달성 성과, 챌린지 기록, 랭킹, 성취 배지 등 개인별 도전 현황을 종합적으로 관리하고 공유하세요."
        canonicalPath="/user-profile-dashboard"
      />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 md:py-8 lg:py-12 mt-16">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">내 프로필</h1>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground">전체 활동 통계와 최근 진행 상황을 확인하세요</p>
        </div>

        <TopTabs tabs={tabs} activeTab={dashboard.activeTab} onChange={dashboard.setActiveTab} />

        {dashboard.activeTab === 'overview' ? (
          <OverviewSection
            overviewTab={dashboard.overviewTab}
            setOverviewTab={dashboard.setOverviewTab}
            goalsProps={{
              levelInfo: dashboard.levelInfo,
              score: dashboard.score,
              statistics: dashboard.statistics,
              safeTrendValue: dashboard.safeTrendValue,
              trendMode: dashboard.trendMode,
              setTrendMode: dashboard.setTrendMode,
              trendYear: dashboard.trendYear,
              setTrendYear: dashboard.setTrendYear,
              availableTrendYears: dashboard.availableTrendYears,
              weeklyTabs: dashboard.weeklyTabs,
              trendWeekIndex: dashboard.trendWeekIndex,
              setTrendWeekIndex: dashboard.setTrendWeekIndex,
              monthlyTrendDataByYear: dashboard.monthlyTrendDataByYear,
              weeklyTrendData: dashboard.weeklyTrendData,
              starDistribution: dashboard.starDistribution,
              streakData: dashboard.streakData,
              lastAttendanceText: dashboard.lastAttendanceText,
              recentProofs: dashboard.recentProofs,
              onOpenProofDetail: dashboard.openProofDetailById,
              isLoading: dashboard.isLoading,
              hasMore: dashboard.hasMore,
              observerTarget: dashboard.observerTarget,
            }}
            destimeProps={{
              destimeStats: dashboard.destimeStats,
              formatDuration: dashboard.formatDuration,
              destimeTrendMode: dashboard.destimeTrendMode,
              setDestimeTrendMode: dashboard.setDestimeTrendMode,
              destimeTrendYear: dashboard.destimeTrendYear,
              setDestimeTrendYear: dashboard.setDestimeTrendYear,
              destimeAvailableYears: dashboard.destimeAvailableYears,
              destimeWeeklyTabs: dashboard.destimeWeeklyTabs,
              destimeTrendWeekIndex: dashboard.destimeTrendWeekIndex,
              setDestimeTrendWeekIndex: dashboard.setDestimeTrendWeekIndex,
              destimeMonthlyTrendData: dashboard.destimeMonthlyTrendData,
              destimeWeeklyTrendData: dashboard.destimeWeeklyTrendData,
              destimeTopActions: dashboard.destimeTopActions,
            }}
          />
        ) : null}

        {dashboard.activeTab === 'achievements' ? (
          <AchievementsSection
            achievementFilters={dashboard.achievementFilters}
            achievementCategory={dashboard.achievementCategory}
            setAchievementCategory={dashboard.setAchievementCategory}
            unlockedBadges={dashboard.unlockedBadges}
            lockedBadges={dashboard.lockedBadges}
          />
        ) : null}

        {dashboard.activeTab === 'neighbors' ? (
          <NeighborsSection
            myNeighbors={dashboard.myNeighbors}
            blockedUsers={dashboard.blockedUsers}
            isNeighborsLoading={dashboard.isNeighborsLoading}
            onRefresh={dashboard.loadNeighborManagement}
            onRemoveNeighbor={dashboard.removeNeighbor}
            onBlockNeighbor={dashboard.blockNeighbor}
            onUnblock={dashboard.unblockUser}
          />
        ) : null}

        {dashboard.activeTab === 'settings' ? (
          <div className="max-w-3xl">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-6 md:mb-8">계정 설정</h2>
            <AccountSettings />
          </div>
        ) : null}

        {dashboard.activeTab === 'image_settings' ? (
          <div className="max-w-5xl">
            <ImagePersonaSettings />
          </div>
        ) : null}

        <ProofDetailModal
          isOpen={dashboard.isProofDetailOpen}
          proofs={dashboard.recentProofs}
          activeIndex={dashboard.activeProofIndex}
          onClose={() => dashboard.setIsProofDetailOpen(false)}
          onPrev={dashboard.handlePrevProof}
          onNext={dashboard.handleNextProof}
          hasPrev={dashboard.activeProofIndex > 0}
          hasNext={dashboard.activeProofIndex < (dashboard.recentProofs?.length || 0) - 1}
        />
      </main>
    </div>
  );
};

export default UserProfileDashboard;